import * as Sentry from "@sentry/react";
import { captureException, setUser as setSentryUser } from "@sentry/react";
import { getLSItem, removeLSItem, setLSItem } from "v4/services/localStorage.service";
import { badCredentials, loadUser, setToken, setUser } from "actions/user";

const VIEW_ORDERS_LS_KEY = 'viewOrders';

let alternativeTranslationsCache = null;
let sentryUserContextApplied = false;

function fetchHandler(res) {
    if (res.status >= 400 && res.status < 600) {
        return Promise.reject(res);
    }
    return res.json();
}

export default class UserService {
    /**
     * Set current user from cache
     * @param {Object} user
     */
    static setUser = ({orders, ...user} = {}) => {
        if (!user.token) {
            user.token = this.getToken();
        }

        if (orders) {
            UserService.setViewOrders(orders['hydra:member']?.[0] ?? {});
        }

        setLSItem('user', user);
        UserService.applySentryUserContext(user);

        UserService.removeCache();
    };

    /**
     * Standardized Sentry user context + tags, kept identical to the backend
     * apps (pol-middleware / pol-api-*): the login is the Sentry "username"
     * (and thus the display label); full_name / customer_name are extra user
     * fields; user_id + customer_id are tags on every transaction and error.
     *
     * Sourced from the decoded JWT claims (login, user_id, email, firstname,
     * lastname, customer_id, customer_name) so the shape matches the backend
     * The /api/user payload already carries these fields (id, login, email,
     * fullname, customer.{id,name}), so we read them straight from it. Note we
     * must NOT call getToken()/getTokenData() here: getToken() itself invokes
     * applySentryUserContext, which would recurse infinitely.
     *
     * @param {Object} user
     */
    static applySentryUserContext = (user = {}) => {
        const userId = user.id ?? null;
        const customerId = user.customer?.id ?? null;

        setSentryUser({
            id: userId,
            username: user.login,
            email: user.email,
            full_name: user.fullname,
            customer_id: customerId,
            customer_name: user.customer?.name ?? null,
            roles: user.roles?.['hydra:member'],
            ip_address: "{{auto}}",
        });

        sentryUserContextApplied = true;

        if (userId != null) {
            Sentry.setTag('user_id', String(userId));
        }
        if (customerId != null) {
            Sentry.setTag('customer_id', String(customerId));
        }
    };

    static removeCache = () => {
        alternativeTranslationsCache = null;
    }

    /**
     * Get current user from cache
     * @returns {Object}
     */
    static getUser = () => {
        const user = getLSItem('user');

        return user?.token ? user : null;
    };

    static getCustomerId = () => {
        const user = getLSItem('user')

        return user?.customer?.id
    }

    static isLiveo = () => {
        const user = getLSItem('user')

        return user && user.customer.isLiveo;
    }

    static hasLiveoConfigured = () => {
        const user = getLSItem('user')

        return user && user.liveoLogin?.length && user.liveoPassword?.length;
    }

    /**
     * Current user has intranet ?
     * @returns {boolean}
     */
    static hasIntranet = () => {
        const user = getLSItem('user')

        return user?.customer?.hasIntranet ?? false;
    }

    /**
     * Remove current user from cache
     */
    static removeUser = () => {
        removeLSItem('user');
        removeLSItem(VIEW_ORDERS_LS_KEY);
        sentryUserContextApplied = false;
    };

    /**
     * Get all the custom tab of User from cache
     * @returns {(Object[]|array)}
     */
    static getTabs = () => {
        const user = getLSItem('user');
        if (user && user.tabs && user.tabs['hydra:member']) {
            return user.tabs['hydra:member'];
        }

        return [];
    };
    static getFlatTabs = () => {
        const user = getLSItem('user');
        if (user && user.tabs && user.tabs['hydra:member']) {
            return user.tabs['hydra:member'].reduce((acc, tab) => {
                if (tab.subMenu && tab.subMenu.length) {
                    acc.push(...tab.subMenu);
                } else {
                    acc.push(tab);
                }

                return acc;
            }, []);
        }

        return [];
    };

    /**
     * Remove one custom tab of User from cache
     * @param {string} removedTabID
     *
     * @returns {(Object[]|array)}
     */
    static removeTab = (removedTabID) => {
        const tabs = UserService.getTabs();

        if (tabs) {
            const indexOfSearch = tabs.findIndex(search => search.id === removedTabID);
            if (indexOfSearch !== -1) {
                tabs.splice(indexOfSearch, 1)
            }

            return UserService.setTabs(tabs);
        }
    }

    /**
     * Set or add one custom tab of User from cache
     * @param {Object} tab
     * @param {string} tab.id
     *
     * @returns {(Object[]|array)}
     */
    static setTab = (tab) => {
        const user = getLSItem('user');
        const tabs = UserService.getTabs();

        if (Object.keys(tab).length && tab.customerId === user.customer.id) {
            let tabFound = tabs.findIndex(el => el.id === tab.id);
            if (-1 === tabFound) {
                tabs.push(tab);

                return UserService.setTabs(tabs);
            }
            tabs[tabFound] = tab;
        }

        return UserService.setTabs(tabs);
    }

    /**
     * Set custom tabs of User from cache
     * @param {Object[]} tabs
     *
     * @returns {Object[]}
     */
    static setTabs = (tabs) => {
        const user = getLSItem('user');
        if (!user.tabs) {
            user.tabs = [];
        }
        user.tabs['hydra:member'] = tabs;
        UserService.setUser(user);

        return user.tabs['hydra:member'];
    }

    /**
     * get slug of the tab with id in argument
     * @param {string} id
     *
     * @returns {string|null}
     */
    static getTabSlugFor = (id) => {
        const tabs = UserService.getTabs();
        const tab = tabs.find(el => el.id === id);

        return -1 === tab || !tab ? null : 'onglet/' + tab.slug;
    }

    static getAlternativeTranslations = () => {
        if (!alternativeTranslationsCache) {
            const user = getLSItem('user');
            alternativeTranslationsCache = user?.customer?.alternativeTranslations ? JSON.parse(user.customer.alternativeTranslations) : {};
        }

        return alternativeTranslationsCache;
    }

    static getSavedSearchs = () => {
        const user = getLSItem('user');
        if (user?.savedUserSearchs?.['hydra:member']) {
            return user.savedUserSearchs['hydra:member'];
        }
    };

    static removeSavedSearch = (removedSearchID) => {
        const user = getLSItem('user');

        if (user && user.savedUserSearchs && user.savedUserSearchs['hydra:member']) {
            const indexOfSearch = user.savedUserSearchs['hydra:member'].findIndex(search => search.id === removedSearchID);
            if (indexOfSearch !== -1) {
                user.savedUserSearchs['hydra:member'].splice(indexOfSearch, 1)
            }
            UserService.setUser(user);
            return user.savedUserSearchs['hydra:member']
        }
    }

    static setSavedSearch = (savedSearch) => {
        const user = getLSItem('user');

        if (Object.keys(savedSearch).length) {
            user.savedUserSearchs['hydra:member'].push(savedSearch)
        }
        UserService.setUser(user)
        return user.savedUserSearchs['hydra:member']
    }

    static setSavedRequest = (savedRequest, entityOrTabId) => {
        const user = getLSItem('user');
        let savedRequests = {};

        if (Object.keys(savedRequest).length) {
            try {
                savedRequests = JSON.parse(user.defaultSearchFilters);
                savedRequests[entityOrTabId] = savedRequest;
                user.defaultSearchFilters = JSON.stringify(savedRequests);
                UserService.setUser(user)
            } catch (e) {
                captureException(e, {
                    message: 'Error while parsing defaultSearchFilters when setting savedRequest in UserService',
                    extra: {
                        user: user,
                        savedRequest: savedRequest,
                        entityOrTabId: entityOrTabId,
                    }
                });
            }
        }
    }

    static setToken = (token, refresh = false) => {
        sentryUserContextApplied = false;

        if (this.isUserSwitching()) {
            this.setSwitchToken(token);
        } else {
            setLSItem('token', token);
            UserService.removeCache();
        }

        if (refresh) {
            document.location.reload(false);
        }
    };

    static setSwitchToken = (token) => {
        sentryUserContextApplied = false;
        setLSItem('token_switch', token);
        UserService.removeCache();
    };

    static removeSwitchToken = () => {
        sentryUserContextApplied = false;
        removeLSItem('token_switch');
        UserService.removeCache();
    };

    static isUserSwitching = () => {
        return !!getLSItem('token_switch', 'string');
    }

    static getRoles = () => {
        const user = getLSItem('user');

        if (user && user.roles && user.roles['hydra:member']) {
            return user.roles['hydra:member'];
        }

        return [];
    };

    static setModulesRoles = (newModulesRoles) => {
        const user = getLSItem('user');
        const noModulesRoles = user.roles['hydra:member'].filter(role => !role.includes('MODULE_'));

        user.roles['hydra:member'] = [...noModulesRoles, ...newModulesRoles];

        UserService.setUser(user);
    }

    static getModulesRoles = () => {
        const user = getLSItem('user');

        if (user && user.roles && user.roles['hydra:member']) {
            return user.roles['hydra:member'].filter(role => role.includes('MODULE_'));
        }

        return [];
    }

    static getAccessRight = () => {
        const user = getLSItem('user');

        if (user && user?.accessRights && user.accessRights['hydra:member']) {
            return user.accessRights['hydra:member'];
        }

        return [];
    }

    /**
     * Check that the current user has at least one of the given roles, or that they are a superAdmin.
     *
     * @param userRoles
     * @param checkSuperAdmin
     *
     * @returns {boolean}
     */
    static controlAccess = (userRoles = ['ROLE_USER'], checkSuperAdmin = true) => {
        const roles = UserService.getRoles();
        if (checkSuperAdmin && roles.indexOf(ROLE_SUPER_ADMIN) !== -1) {
            return true;
        }
        return userRoles.some(userRole => roles.includes(userRole));
    };

    static submitFormSearchOnLoading = (orderEntity) => {
        const ROLES = {
            Quote: 'MY_QUOTE_BY_DEFAULT',
            Task: 'MY_TASK_BY_DEFAULT',
            Prospect: 'MY_PROSPECT_BY_DEFAULT',
            Contact: 'MY_CONTACT_BY_DEFAULT',
        }
        return UserService.controlAccess([ROLES[orderEntity]], false)
    }

    static getToken = () => {
        const token = getLSItem('token_switch', 'string') || getLSItem('token', 'string');

        if (token && !sentryUserContextApplied) {
            const user = getLSItem('user');

            if (user?.id) {
                UserService.applySentryUserContext(user);
            }

        }

        return token;
    };

    static removeToken = () => {
        removeLSItem('token');

        UserService.removeCache();
    };

    static getOriginalToken = () => {
        return getLSItem('token', 'string');
    };

    static getGlobalSearch = () => {
        if (getLSItem('global_search')) {
            return getLSItem('global_search');
        }
        return null;
    };

    static setGlobalSearch = (search) => {
        setLSItem('global_search', search);
    };

    /**
     * ViewOrders are stored under their own localStorage key: they weigh about 130 KB,
     * where the rest of the user weighs 9 KB, and getLSItem structuredClones whatever it
     * returns. Keeping them inside 'user' made every getToken() clone them.
     *
     * @returns {Object|null}
     */
    static getViewOrders = () => {
        const viewOrders = getLSItem(VIEW_ORDERS_LS_KEY);

        if (Object.keys(viewOrders).length) {
            return viewOrders;
        }

        return UserService.migrateViewOrdersOutOfUser();
    }

    /**
     * @param {Object} viewOrders
     */
    static setViewOrders = (viewOrders) => {
        if (!viewOrders) return;

        setLSItem(VIEW_ORDERS_LS_KEY, viewOrders);
    }

    /**
     * Sessions opened before the split still carry viewOrders inside 'user'. Move them
     * on first read instead of forcing a re-login.
     *
     * @returns {Object|null}
     */
    static migrateViewOrdersOutOfUser = () => {
        const {orders, ...user} = getLSItem('user');
        const viewOrders = orders?.['hydra:member']?.[0];

        if (!viewOrders) return null;

        UserService.setViewOrders(viewOrders);
        setLSItem('user', user);

        return viewOrders;
    }

    static isLastVisit = (key) => {
        const listlastVisits = ['contacts'];

        if (listlastVisits.length) {
            const keyExist = listlastVisits.filter(item => item === key);

            if (keyExist.length) {
                return true;
            }
        }

        return false;
    };

    static logout = () => {
        UserService.removeToken();
        UserService.removeSwitchToken();
        UserService.removeCache();
        UserService.removeUser();
    }

    static isLogged = () => {
        return !!UserService.getToken() && !UserService.isSSOOnly();
    }

    static getLocale = () => {
        const locale = getLSItem('i18nextLng', 'string');
        if (locale.length > 0) return locale;

        setLSItem('i18nextLng', 'fr-FR');
        return 'fr-FR';
    }

    static setRedirectTo = (url) => {
        this.logout(); // Ensure user is logged out before setting redirect
        setLSItem('redirectTo', url);
    }

    /** @returns {string|null} */
    static getRedirectTo = () => {
        return getLSItem('redirectTo', 'string');
    }

    static removeRedirectTo = () => {
        removeLSItem('redirectTo');
    }

    /**
     * @return {{
     * label: string,
     * title: string,
     * icon: string,
     * action: string,
     * type: string,
     * confirmMessage: string,
     * confirmBeforeMessage: string,
     * groupCode: string,
     * component: null | string
     * }[]}
     */
    static getCustomActions = () => {
        const user = getLSItem('user');
        if (!user?.customActions?.['hydra:member']) return [];

        return user.customActions['hydra:member'];
    }

    static getTokenData = () => {
        const token = UserService.getToken();

        if (!token) return null;

        try {
            const tokenDataPart = token.split('.')[1];
            return JSON.parse(atob(tokenDataPart.replace(/-/g, '+').replace(/_/g, '/')));
        } catch (e) {
            console.error('Error while parsing token data', e);
            captureException(e, {
                message: 'Error while parsing token data in UserService',
                extra: {
                    token: token,
                }
            });
            return null;
        }
    }

    static isTokenExpired = () => {
        const tokenData = this.getTokenData();
        if (!tokenData || !tokenData.exp) return true;

        const currentTime = Math.floor(Date.now() / 1000);
        return tokenData.exp < currentTime;
    }

    static isSSOOnly = () => {
        return this.getTokenData()?.sso_only === true;
    }

    static getA11ySettings = () => {
        return getLSItem('getA11ySettings') ?? {};
    }

    static setA11ySettings = (settings) => {
        setLSItem('getA11ySettings', settings);
    }

    static getA11ySetting = (settingKey) => {
        const settings = this.getA11ySettings;

        return settings?.[settingKey] ?? null;
    }

    static setA11ySetting = (settingKey, settingValue) => {
        const settings = this.getA11ySettings;
        settings[settingKey] = settingValue;
        setLSItem('getA11ySettings', settings);
    }
};

function getTime(dispatch, error = false, data = []) {
    if (!error && data.user) {
        dispatch(setUser(data.user));
    }

    setTimeout(function () {
        dispatch(loadUser(false));

        if (!error) {
            if (data.token) {
                dispatch(setToken(data.token));
            } else {
                dispatch(badCredentials({
                    status: 500,
                    data: 'No datas return'
                }));
            }
        } else {
            dispatch(badCredentials(error));
        }
    }, 500)
}

export function LoginService(info, setToken) {
    return function (dispatch) {
        dispatch(loadUser(true));

        return fetch(process.env.REACT_APP_HOST_API + '/api/login', {
            method: 'POST',
            headers: {
                Accept: 'application/ld+json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(info),
        }).then(
            fetchHandler,
            error => {
                getTime(dispatch, {
                    status: 500,
                    data: error
                });
            }
        ).then(
            data => {
                if (data) {
                    setToken(data.token);
                    dispatch(InfoService(data))
                }
            },
            error => {
                getTime(dispatch, error);
            }
        );
    };
}

export function InfoService(loginData) {
    return function (dispatch) {
        return fetch(process.env.REACT_APP_HOST_API + '/api/user', {
            method: 'GET',
            headers: {
                Accept: 'application/ld+json',
                'Content-Type': 'application/json',
                "X-POL-AUTH": 'Bearer ' + loginData.token
            },
        }).then(
            fetchHandler,
            error => {
                getTime(dispatch, {
                    status: 500,
                    data: error
                });
            }
        ).then(
            data => {
                getTime(dispatch, false, { user: data, ...loginData });
            },
        );
    };
}

export const ROLE_SUPER_ADMIN = 'ROLE_SUPERADMIN';
export const ROLE_ADMIN = 'ROLE_ADMIN';
export const ROLE_MODULE_PRODUCT_CUSTOMIZE = 'ROLE_MODULE_PRODUCT_CUSTOMIZE';
